import type {
  Account,
  Alert,
  BudgetStatus,
  Category,
  GoalProgress,
  ImportBatchSummary,
  Insight,
  TransactionRow,
} from '@shared/types';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (res.status === 401) {
    // Tentative refresh une fois
    const refreshed = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    if (refreshed.ok) {
      const retry = await fetch(path, { credentials: 'include', ...init });
      if (!retry.ok) await throwApi(retry);
      if (retry.status === 204) return undefined as T;
      return (await retry.json()) as T;
    }
    await throwApi(res);
  }
  if (!res.ok) await throwApi(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function throwApi(res: Response): Promise<never> {
  let body: { error?: { code?: string; message?: string; details?: unknown } } = {};
  try {
    body = await res.json();
  } catch {
    // ignore
  }
  throw new ApiError(
    res.status,
    body.error?.code ?? 'UNKNOWN',
    body.error?.message ?? `Erreur HTTP ${res.status}`,
    body.error?.details,
  );
}

function qs(o: Record<string, unknown>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) s.set(k, v.join(','));
    else s.set(k, String(v));
  }
  const str = s.toString();
  return str ? `?${str}` : '';
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<{ user: { email: string }; accessToken: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<{ user: { email: string | null } }>('/api/auth/me'),

  // Accounts
  listAccounts: () => request<Account[]>('/api/accounts'),

  // Categories
  listCategories: () => request<Category[]>('/api/categories'),
  createCategory: (data: Partial<Category>) =>
    request<Category>('/api/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id: string, data: Partial<Category>) =>
    request<Category>(`/api/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategory: (id: string) => request<void>(`/api/categories/${id}`, { method: 'DELETE' }),

  // Transactions
  listTransactions: (filters: Record<string, unknown> = {}) =>
    request<{ rows: TransactionRow[]; total: number; limit: number; offset: number }>(
      `/api/transactions${qs(filters)}`,
    ),
  setCategoryOnTx: (id: string, categoryId: string | null) =>
    request(`/api/transactions/${id}/category`, { method: 'PATCH', body: JSON.stringify({ categoryId }) }),
  setNotesOnTx: (id: string, notes: string) =>
    request(`/api/transactions/${id}/notes`, { method: 'PATCH', body: JSON.stringify({ notes }) }),
  recategorizeTx: (id: string) => request(`/api/transactions/${id}/recategorize`, { method: 'POST' }),
  markNotAnomaly: (id: string) =>
    request(`/api/transactions/${id}/mark-not-anomaly`, { method: 'PATCH' }),

  // Imports
  listImports: () => request<unknown[]>('/api/imports'),
  uploadCamt: async (file: File): Promise<ImportBatchSummary> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/imports', { method: 'POST', body: fd, credentials: 'include' });
    if (!res.ok) await throwApi(res);
    return (await res.json()) as ImportBatchSummary;
  },

  // Budgets
  listBudgets: () => request<BudgetStatus[]>('/api/budgets'),
  createBudget: (data: unknown) =>
    request<BudgetStatus>('/api/budgets', { method: 'POST', body: JSON.stringify(data) }),
  updateBudget: (id: string, data: unknown) =>
    request<BudgetStatus>(`/api/budgets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBudget: (id: string) => request<void>(`/api/budgets/${id}`, { method: 'DELETE' }),

  // Goals
  listGoals: () => request<GoalProgress[]>('/api/goals'),
  createGoal: (data: unknown) =>
    request<GoalProgress>('/api/goals', { method: 'POST', body: JSON.stringify(data) }),
  updateGoal: (id: string, data: unknown) =>
    request<GoalProgress>(`/api/goals/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGoal: (id: string) => request<void>(`/api/goals/${id}`, { method: 'DELETE' }),
  reviewGoal: (id: string) =>
    request<{ content: string; progress: GoalProgress }>(`/api/goals/${id}/review`, { method: 'POST' }),

  // Alerts
  listAlerts: (unreadOnly = false) =>
    request<Alert[]>(`/api/alerts${qs({ unread_only: unreadOnly })}`),
  markAlertRead: (id: string) => request<Alert>(`/api/alerts/${id}/read`, { method: 'PATCH' }),
  dismissAlert: (id: string) => request<Alert>(`/api/alerts/${id}/dismiss`, { method: 'PATCH' }),
  markAllAlertsRead: () => request<{ updated: number }>('/api/alerts/mark-all-read', { method: 'POST' }),

  // Insights
  listInsights: (type?: string) => request<Insight[]>(`/api/insights${qs({ type })}`),
  getInsight: (id: string) => request<Insight>(`/api/insights/${id}`),
  generateBilan: (type: 'weekly_bilan' | 'monthly_bilan') =>
    request<Insight>('/api/insights/generate', { method: 'POST', body: JSON.stringify({ type }) }),
  dashboardKpis: (from: string, to: string) =>
    request<{
      kpis: { income: number; expense: number; net: number; txCount: number };
      byCategory: Array<{ categoryId: string | null; categoryName: string | null; total: number; count: number }>;
      topMerchants: Array<{ counterparty: string; total: number; count: number }>;
      daily: Array<{ day: string; net: number }>;
    }>(`/api/insights/dashboard/kpis${qs({ from, to })}`),

  // Chat
  sendChat: (message: string, conversationId?: string) =>
    request<{ conversationId: string; reply: string; toolCallsUsed: Array<{ name: string; input: unknown }> }>(
      '/api/chat',
      { method: 'POST', body: JSON.stringify({ message, conversationId }) },
    ),
  listConversations: () =>
    request<Array<{ conversation_id: string; first_message: string; first_at: string; last_at: string; n: number }>>(
      '/api/chat/conversations',
    ),
  getConversation: (id: string) =>
    request<Array<{ id: string; role: 'user' | 'assistant' | 'tool'; content: string; toolCalls: unknown; toolResults: unknown; createdAt: string }>>(
      `/api/chat/conversations/${id}`,
    ),
};
